import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { addPending } from "@/lib/outbox";
import { Square, Sparkles, WifiOff } from "lucide-react";

const STEPS = ["Transcription Whisper…", "Analyse GPT & probabilité de signature…", "Rattachement à la fiche…"];

export default function VoiceDebriefCard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState("idle");
  const [secs, setSecs] = useState(0);
  const [step, setStep] = useState(0);
  const rec = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const saveOffline = async (blob, ext) => {
    await addPending({ kind: "voice", eventName: localStorage.getItem("klt_event") || "Terrain", captureMethod: "VOICE", audioBlob: blob, audioName: `debrief.${ext}` });
    window.dispatchEvent(new Event("klt-outbox-changed"));
    toast.success("Pas de réseau : votre débrief audio est gardé en sécurité sur le téléphone. Dès le retour de la connexion, l'IA le transcrit et le range dans la bonne fiche prospect (ou en crée une).", { duration: 8000 });
    setState("idle");
  };

  const process = async (blob) => {
    const ext = blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm";
    if (!navigator.onLine) { await saveOffline(blob, ext); return; }
    setState("processing"); setStep(0);
    try {
      const fd = new FormData();
      fd.append("file", blob, `debrief.${ext}`);
      fd.append("contactInfo", "{}");
      const { data: dr } = await api.post("/audio-debrief", fd);
      setStep(1);
      const tr = (dr.debrief?.transcript || "").trim();
      if (tr.length < 15) throw new Error("Débrief trop court : parlez au moins quelques phrases.");
      const { data: m } = await api.post("/leads/voice-match", { transcript: tr });
      const c0 = m.contact || {};
      if (!m.lead && !c0.firstName && !c0.lastName && !c0.company) throw new Error("Aucun prospect identifié : citez son nom ou son entreprise.");
      setStep(2);
      let id;
      if (m.lead) {
        await api.post(`/leads/${m.lead.id}/debriefs`, { debrief: dr.debrief, aiClassification: dr.aiClassification });
        id = m.lead.id;
        const c = m.lead.contactInfo || {};
        toast.success(`Débrief ajouté à la fiche de ${[c.firstName, c.lastName].filter(Boolean).join(" ") || c.company} ⚡`);
      } else {
        const { data: l } = await api.post("/leads", { eventName: localStorage.getItem("klt_event") || "Terrain", captureMethod: "VOICE", contactInfo: m.contact, debriefs: [dr.debrief], aiClassification: dr.aiClassification });
        id = l.id;
        toast.success("Nouvelle fiche prospect créée par l'IA ⚡");
      }
      window.dispatchEvent(new CustomEvent("birdie:changed"));
      navigate(`/lead/${id}`);
    } catch (e) {
      if (e.code === "ERR_NETWORK" || !navigator.onLine) { await saveOffline(blob, ext); return; }
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(" · ") : d || e.message || "Débrief impossible, réessayez");
      setState("idle");
    }
  };

  const start = async () => {
    if (state !== "idle") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      mr.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      mr.onstop = () => { stream.getTracks().forEach((t) => t.stop()); process(new Blob(chunks.current, { type: mr.mimeType || "audio/webm" })); };
      if (!navigator.onLine) toast.info("Pas de réseau ici : parlez normalement, l'audio sera gardé sur le téléphone et analysé dès le retour de la connexion.", { duration: 6000 });
      mr.start(); rec.current = mr; setSecs(0); setState("recording");
      timer.current = setInterval(() => setSecs((s) => { if (s >= 179) { stop(); } return s + 1; }), 1000);
    } catch { toast.error("Micro inaccessible : autorisez-le dans votre navigateur"); }
  };
  const stop = () => { clearInterval(timer.current); if (rec.current?.state === "recording") rec.current.stop(); };

  useEffect(() => { if (location.state?.voice) { navigate(".", { replace: true, state: {} }); start(); } }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => clearInterval(timer.current), []);

  const rec_ = state === "recording";
  return (
    <div className="col-span-2 klt-glow-card rounded-3xl p-5 sm:p-6 flex items-center gap-5 klt-fade-up" data-testid="voice-debrief-card">
      <button onClick={rec_ ? stop : start} disabled={state === "processing"} data-testid="voice-debrief-btn" aria-label={rec_ ? "Arrêter" : "Démarrer le débrief vocal"}
        className={`relative shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-full flex items-center justify-center text-white transition-transform active:scale-95 ${rec_ ? "bg-rose-500 klt-rec-pulse" : state === "processing" ? "klt-fab-neon klt-ai-halo" : "klt-fab-neon"}`}>
        {state === "processing" ? <Sparkles className="w-10 h-10 klt-sparkle" data-testid="voice-debrief-sparkle" /> : rec_ ? <Square className="w-9 h-9" fill="currentColor" /> : <Sparkles className="w-10 h-10" />}
      </button>
      <div className="min-w-0">
        <p className="font-heading text-xl sm:text-2xl font-extrabold">Débrief vocal IA</p>
        <p className="text-sm text-muted-foreground mt-0.5" data-testid="voice-debrief-status">
          {state === "idle" && (online ? "Synthèse automatique par Whisper + GPT · appuyez et parlez 30 s" : <span className="flex items-start gap-1.5 text-amber-400 font-medium" data-testid="voice-debrief-offline"><WifiOff className="w-4 h-4 shrink-0 mt-0.5" />Pas de réseau : enregistrez quand même, l'audio est gardé et analysé au retour de la connexion</span>)}
          {rec_ && <span className="text-rose-500 font-semibold">● Enregistrement {String(Math.floor(secs / 60)).padStart(2, "0")}:{String(secs % 60).padStart(2, "0")} — appuyez pour terminer</span>}
          {state === "processing" && <span className="text-[#60A5FA] font-semibold">{STEPS[step]}</span>}
        </p>
      </div>
    </div>
  );
}
